import React, { useEffect, useState } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useManufacturers } from "@/hooks/useManufacturers";
import { useMarkets } from "@/hooks/useMarkets";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { GripVertical, Plus, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";

interface ManufacturerData {
  maker_id: number;
  manufacturer_name: string;
  ranking?: number;
  market?: string;
}

const ManufacturerRanking = () => {
  const { masterData, adData, atpData, refetchMaster } = useManufacturers();
  const uniqueMarkets = useMarkets(masterData, adData, atpData);

  const [selectedMarket, setSelectedMarket] = useState<string | undefined>(undefined);
  const [editableRanking, setEditableRanking] = useState<ManufacturerData[]>([]);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<{ad: boolean, atp: boolean}>({ ad: false, atp: false });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<ManufacturerData[]>([]);

  useEffect(() => {
    if (
      (!selectedMarket || !uniqueMarkets.includes(selectedMarket)) &&
      uniqueMarkets.length
    ) {
      setSelectedMarket(uniqueMarkets[0]);
    }
  }, [uniqueMarkets, selectedMarket]);

  useEffect(() => {
    if (selectedMarket && masterData) {
      const filteredData = masterData
        .filter(item => item.market === selectedMarket)
        .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
        .slice(0, 10);
      
      setEditableRanking(filteredData);
    }
  }, [selectedMarket, masterData]);

  useEffect(() => {
    if (searchQuery.length > 1 && masterData && selectedMarket) {
      const results = masterData
        .filter(item => 
          item.market === selectedMarket && 
          item.manufacturer_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !editableRanking.some(rank => rank.maker_id === item.maker_id)
        )
        .slice(0, 5);
      
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, masterData, selectedMarket, editableRanking]);

  const getTop10ByMarket = (data: ManufacturerData[] = [], market: string | undefined) => {
    if (!market) return [];
    
    return data
      .filter(item => item.market === market)
      .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
      .slice(0, 10);
  };

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newItems = [...editableRanking];
    const draggedItemValue = newItems[draggedItem];
    
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedItemValue);
    
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      ranking: idx + 1
    }));
    
    setEditableRanking(updatedItems);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    setDraggedItem(null);
    
    try {
      for (const item of editableRanking) {
        const { error } = await supabase
          .from('vehicledb_master')
          .update({ ranking: item.ranking })
          .eq('maker_id', item.maker_id)
          .eq('market', selectedMarket);

        if (error) throw error;
      }
      
      toast.success("Rankings updated successfully");
      refetchMaster();
    } catch (error) {
      console.error('Error updating rankings:', error);
      toast.error("Failed to update rankings");
    }
  };

  const syncRankings = async (sourceTable: 'ad' | 'atp') => {
    if (!selectedMarket) {
      toast.error("Please select a market first");
      return;
    }
    
    try {
      setIsLoading({ ...isLoading, [sourceTable]: true });
      console.log(`Starting sync from ${sourceTable} data for market: ${selectedMarket}`);
      
      const sourceData = sourceTable === 'ad' ? adData : atpData;
      const filteredSourceData = sourceData.filter(item => item.market === selectedMarket);
      
      console.log(`Found ${filteredSourceData.length} items to sync from ${sourceTable}`);
      
      if (filteredSourceData.length === 0) {
        toast.error(`No ${sourceTable.toUpperCase()} data available for the selected market`);
        setIsLoading({ ...isLoading, [sourceTable]: false });
        return;
      }
      
      for (const item of filteredSourceData) {
        console.log(`Updating item: maker_id=${item.maker_id}, ranking=${item.ranking}`);
        
        const { error } = await supabase
          .from('vehicledb_master')
          .update({ ranking: item.ranking || 999 })
          .eq('maker_id', item.maker_id)
          .eq('market', selectedMarket);
          
        if (error) {
          console.error(`Error updating item ${item.maker_id}:`, error);
          throw error;
        }
      }

      toast.success(`Rankings synchronized with ${sourceTable.toUpperCase()} data`);
      await refetchMaster();
    } catch (error) {
      console.error('Error syncing rankings:', error);
      toast.error("Failed to synchronize rankings");
    } finally {
      setIsLoading({ ...isLoading, [sourceTable]: false });
    }
  };

  const addManufacturerToRanking = async (manufacturer: ManufacturerData) => {
    if (!selectedMarket) {
      toast.error("Please select a market first");
      return;
    }

    try {
      const newRanking = 10;
      
      const currentRank10Manufacturer = editableRanking.find(m => m.ranking === 10);
      
      if (currentRank10Manufacturer) {
        const { error: resetError } = await supabase
          .from('vehicledb_master')
          .update({ ranking: 0 })
          .eq('maker_id', currentRank10Manufacturer.maker_id)
          .eq('market', selectedMarket);
        
        if (resetError) {
          console.error('Error resetting previous rank 10:', resetError);
          throw resetError;
        }
      }
      
      const { error } = await supabase
        .from('vehicledb_master')
        .update({ ranking: newRanking })
        .eq('maker_id', manufacturer.maker_id)
        .eq('market', selectedMarket);
      
      if (error) throw error;

      const updatedManufacturer = {
        ...manufacturer,
        ranking: newRanking
      };
      
      setEditableRanking(prev => {
        const newRankingArray = prev.map(item => {
          if (item.ranking === 10) {
            return { ...item, ranking: 0 };
          }
          return item;
        });
        
        const combinedRanking = [...newRankingArray, updatedManufacturer]
          .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
          .slice(0, 10);
        
        return combinedRanking;
      });

      setSearchQuery("");
      setSearchResults([]);
      
      toast.success(`${manufacturer.manufacturer_name} added to the rankings`);
      
      await refetchMaster();
    } catch (error) {
      console.error('Error adding manufacturer to rankings:', error);
      toast.error("Failed to add manufacturer to rankings");
    }
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">Manufacturer Ranking</h1>
      
      <div className="mb-6">
        <Select value={selectedMarket} onValueChange={setSelectedMarket}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select market" />
          </SelectTrigger>
          <SelectContent>
            {uniqueMarkets.map((market) => (
              <SelectItem key={market} value={market}>
                {market}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Top 10 AD Rankings</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => syncRankings('ad')}
              disabled={isLoading.ad}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading.ad ? 'animate-spin' : ''}`} />
              {isLoading.ad ? 'Syncing...' : 'Sync'}
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getTop10ByMarket(adData, selectedMarket).map((item, index) => (
                  <TableRow key={`ad-${item.maker_id}`}>
                    <TableCell>{item.ranking || index + 1}</TableCell>
                    <TableCell>{item.manufacturer_name}</TableCell>
                    <TableCell>{item.maker_id}</TableCell>
                  </TableRow>
                ))}
                {getTop10ByMarket(adData, selectedMarket).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-4">No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Top 10 ATP Rankings</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => syncRankings('atp')}
              disabled={isLoading.atp}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading.atp ? 'animate-spin' : ''}`} />
              {isLoading.atp ? 'Syncing...' : 'Sync'}
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getTop10ByMarket(atpData, selectedMarket).map((item, index) => (
                  <TableRow key={`atp-${item.maker_id}`}>
                    <TableCell>{item.ranking || index + 1}</TableCell>
                    <TableCell>{item.manufacturer_name}</TableCell>
                    <TableCell>{item.maker_id}</TableCell>
                  </TableRow>
                ))}
                {getTop10ByMarket(atpData, selectedMarket).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-4">No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Edit Master Rankings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-ring">
                <Search className="absolute left-2 h-4 w-4 text-muted-foreground" />
                <Input 
                  type="text"
                  placeholder="Search manufacturers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              
              {searchResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
                  <ul className="py-1">
                    {searchResults.map((item) => (
                      <li 
                        key={`search-${item.maker_id}`}
                        className="flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer"
                      >
                        <span>{item.manufacturer_name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addManufacturerToRanking(item)}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="h-4 w-4" />
                          <span className="sr-only">Add to rankings</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Rank</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editableRanking.map((item, index) => (
                  <TableRow 
                    key={`editable-${item.maker_id}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={draggedItem === index ? "opacity-50 cursor-grabbing" : "cursor-grab"}
                  >
                    <TableCell>
                      <GripVertical className="h-5 w-5 text-muted-foreground" />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.manufacturer_name}</TableCell>
                    <TableCell>{item.maker_id}</TableCell>
                  </TableRow>
                ))}
                {editableRanking.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Drag and drop items in the "Edit Master Rankings" table to adjust the ranking order.
      </p>
    </div>
  );
};

export default ManufacturerRanking;
